import { useUserStore } from '@/stores/useUserStore';
import { LocalStorageKey, LocalStorageService } from '@template/utils';
import { ref, computed } from 'vue';

/**
 * 다이얼로그 타입 정의
 * 새로운 다이얼로그를 추가할 때 여기에 타입을 추가하세요
 */
export type GlobalDialogType =
  | 'loginNeeded'
  | 'openApiLoginNeeded'
  | 'openApiAccountNeeded'
  | 'accountPassword'
  | 'accountPasswordSetup'
  | 'memberInfoEdit'
  | 'resetPassword'
  | 'accountPasswordInput'
  | 'accountPasswordResetInput'
  | 'accountPasswordResetConfirm'
  | 'accountPasswordFailExceed'
  | 'accountPasswordResetMismatch'
  | 'accountPasswordResetComplete'
  | 'accountCreateLeave'
  | 'documentSubmissionGuide'
  | 'createAccount';

/**
 * 각 다이얼로그별 페이로드 타입 정의
 * 콜백이 필요한 다이얼로그는 여기에 페이로드 타입을 추가하세요
 */
export type AccountPasswordConfirmPayload = {
  quickAuthEnabled: boolean;
  password?: string;
};

export type AccountPasswordInputConfirmPayload = {
  password: string;
};

/**
 * accountPasswordInput 다이얼로그 옵션
 */
export type AccountPasswordInputOptions = {
  isOpenApi?: boolean;
};

export type AccountPasswordResetInputConfirmPayload = {
  password: string;
};

export type AccountCreateLeaveConfirmPayload = {
  confirmed: boolean;
};

/**
 * 다이얼로그별 콜백 핸들러 타입 매핑
 * 콜백이 없는 다이얼로그는 never로 정의하세요
 */
export type DialogPayloadMap = {
  accountPassword: AccountPasswordConfirmPayload;
  accountPasswordSetup: AccountPasswordConfirmPayload;
  accountPasswordInput: AccountPasswordInputConfirmPayload;
  accountPasswordResetInput: AccountPasswordResetInputConfirmPayload;
  accountCreateLeave: AccountCreateLeaveConfirmPayload;
  // 콜백이 없는 다이얼로그는 never로 정의
  loginNeeded: never;
  openApiLoginNeeded: never;
  openApiAccountNeeded: never;
  memberInfoEdit: never;
  resetPassword: never;
  accountPasswordResetConfirm: never;
  accountPasswordFailExceed: never;
  accountPasswordResetMismatch: never;
  accountPasswordResetComplete: never;
  documentSubmissionGuide: never;
  createAccount: never;
};

/**
 * 다이얼로그 상태 인터페이스
 * @template T - 다이얼로그별 페이로드 타입
 */
interface DialogState<T = never> {
  isOpen: boolean;
  callback?: ((payload: T) => void) | null;
  options?: Record<string, unknown>;
}

/**
 * 모든 다이얼로그의 상태를 관리하는 맵
 * 콜백이 필요한 다이얼로그는 callback 필드를 사용합니다
 */
const dialogStates = ref<{
  [K in GlobalDialogType]: DialogState<DialogPayloadMap[K]>;
}>({
  loginNeeded: { isOpen: false },
  openApiLoginNeeded: { isOpen: false },
  openApiAccountNeeded: { isOpen: false },
  accountPassword: { isOpen: false, callback: null },
  accountPasswordSetup: { isOpen: false, callback: null },
  accountPasswordInput: { isOpen: false, callback: null },
  accountPasswordResetInput: { isOpen: false, callback: null },
  accountCreateLeave: { isOpen: false, callback: null },
  memberInfoEdit: { isOpen: false },
  resetPassword: { isOpen: false },
  accountPasswordResetConfirm: { isOpen: false },
  accountPasswordFailExceed: { isOpen: false },
  accountPasswordResetMismatch: { isOpen: false },
  accountPasswordResetComplete: { isOpen: false },
  documentSubmissionGuide: { isOpen: false },
  createAccount: { isOpen: false },
});

/**
 * 다이얼로그 매니저 인스턴스 타입
 */
type DialogManagerInstance = ReturnType<typeof createDialogManager>;

type AccountPasswordSetupCallback = (payload: AccountPasswordConfirmPayload) => void | Promise<void>;

/**
 * 계좌 비밀번호 셋업 다이얼로그가 실제로 열릴 때만 1회 노출 플래그 제거 (지연 오픈 전에는 키 유지)
 */
const clearAccountPasswordPopupNeededIfSet = () => {
  if (LocalStorageService.getItem(LocalStorageKey.ACCOUNT_PASSWORD_POPUP_NEEDED)) {
    LocalStorageService.removeItem(LocalStorageKey.ACCOUNT_PASSWORD_POPUP_NEEDED);
  }
};

/**
 * 다이얼로그 매니저 팩토리 함수
 */
function createDialogManager() {
  /**
   * 서류 제출 가이드가 열려 있을 때 지연되는 accountPasswordSetup 콜백
   */
  const pendingAccountPasswordSetupCallback = ref<AccountPasswordSetupCallback | null>(null);

  /**
   * 서류 제출하러 가기(확인) 시: 지연된 계좌 비밀번호 셋업을 취소
   */
  const clearPendingAccountPasswordSetup = () => {
    pendingAccountPasswordSetupCallback.value = null;
  };

  /**
   * 서류 제출 가이드 닫기(X 등) 시: 지연된 계좌 비밀번호 셋업을 실제로 연다
   */
  const flushPendingAccountPasswordSetup = () => {
    const cb = pendingAccountPasswordSetupCallback.value;
    if (!cb) return;
    pendingAccountPasswordSetupCallback.value = null;
    const setup = dialogStates.value.accountPasswordSetup;
    setup.isOpen = true;
    setup.callback = cb;
    clearAccountPasswordPopupNeededIfSet();
  };

  /**
   * 특정 다이얼로그 열기
   * @param type - 열고자 하는 다이얼로그 타입
   * @param callback - 콜백이 필요한 다이얼로그의 경우 콜백 함수 (옵션)
   * @param options - accountPasswordInput의 경우 isOpenApi 등 옵션 전달 가능
   */
  const open = <T extends GlobalDialogType>(
    type: T,
    ...args: T extends 'accountPasswordInput'
      ? [callback: (payload: DialogPayloadMap[T]) => void, options?: AccountPasswordInputOptions]
      : DialogPayloadMap[T] extends never
        ? []
        : [callback: (payload: DialogPayloadMap[T]) => void]
  ) => {
    if (type === 'accountPasswordSetup') {
      const callback = args[0] as AccountPasswordSetupCallback;
      if (dialogStates.value.documentSubmissionGuide.isOpen && callback) {
        pendingAccountPasswordSetupCallback.value = callback;
        return;
      }
    }

    dialogStates.value[type].isOpen = true;
    if (args.length > 0 && 'callback' in dialogStates.value[type]) {
      dialogStates.value[type].callback = args[0] as (payload: DialogPayloadMap[T]) => void;
    }
    if (type === 'accountPasswordInput' && args[1]) {
      dialogStates.value[type].options = args[1];
    }
    if (type === 'accountPasswordSetup') {
      clearAccountPasswordPopupNeededIfSet();
    }
  };

  /**
   * 특정 다이얼로그 닫기
   * @param type - 닫고자 하는 다이얼로그 타입
   */
  const close = (type: GlobalDialogType) => {
    const state = dialogStates.value[type];
    state.isOpen = false;
    if ('callback' in state) {
      state.callback = null;
    }
    if ('options' in state) {
      state.options = undefined;
    }
    if (type === 'documentSubmissionGuide') {
      flushPendingAccountPasswordSetup();
    }
  };

  /**
   * 특정 다이얼로그의 열림 상태 확인
   * @param type - 확인하고자 하는 다이얼로그 타입
   * @returns 다이얼로그가 열려있는지 여부
   */
  const isOpen = (type: GlobalDialogType): boolean => {
    return dialogStates.value[type].isOpen;
  };

  /**
   * 특정 다이얼로그의 열림 상태를 반환하는 computed
   * v-model과 함께 사용하기 위한 용도
   * @param type - 확인하고자 하는 다이얼로그 타입
   * @returns 다이얼로그 열림 상태의 computed ref
   */
  const getDialogState = (type: GlobalDialogType) => {
    return computed({
      get: () => dialogStates.value[type].isOpen,
      set: (value: boolean) => {
        if (value) {
          dialogStates.value[type].isOpen = true;
        } else {
          close(type);
        }
      },
    });
  };

  /**
   * 다이얼로그 확인 처리 (콜백 실행)
   * @param type - 다이얼로그 타입
   * @param payload - 확인 시 전달되는 페이로드
   * @param shouldClose - 다이얼로그를 닫을지 여부 (기본값: true)
   */
  const resolve = <T extends GlobalDialogType>(
    type: T,
    payload: DialogPayloadMap[T] extends never ? never : DialogPayloadMap[T],
    shouldClose: boolean = true
  ) => {
    const state = dialogStates.value[type];
    if (state.callback && 'callback' in state) {
      (state.callback as (payload: DialogPayloadMap[T]) => void)(payload as DialogPayloadMap[T]);
    }
    if (shouldClose) {
      close(type);
    }
  };

  /**
   * 모든 다이얼로그 닫기
   */
  const closeAll = () => {
    pendingAccountPasswordSetupCallback.value = null;
    Object.keys(dialogStates.value).forEach((key) => {
      const type = key as GlobalDialogType;
      close(type);
    });
  };

  /**
   * 로그인 여부 확인 및 필요시 모달 열기
   * @param onBlocked - 차단 시 실행할 콜백 (옵션)
   * @returns 로그인되어 있으면 true, 아니면 false
   */
  const ensureLoggedIn = (onBlocked?: () => void): boolean => {
    const userStore = useUserStore();
    const loggedIn = userStore.isLoggedIn;
    if (!loggedIn) {
      open('loginNeeded');
      onBlocked?.();
      return false;
    }
    return true;
  };

  return {
    // methods
    open,
    close,
    isOpen,
    getDialogState,
    resolve,
    closeAll,
    clearPendingAccountPasswordSetup,
    flushPendingAccountPasswordSetup,
    // 로그인 필요 다이얼로그 전용 메서드
    ensureLoggedIn,
    // 내부 상태 접근 (v-model 호환을 위해)
    dialogStates,
  };
}

/**
 * 전역 다이얼로그 매니저 인스턴스 (싱글톤)
 */
let globalDialogManagerInstance: DialogManagerInstance | null = null;

/**
 * 전역 다이얼로그 상태 관리 컴포저블
 * 여러 다이얼로그를 중앙에서 관리하고 확장 가능한 구조를 제공합니다.
 * 싱글톤 패턴으로 동작하여 앱 전체에서 동일한 인스턴스를 공유합니다.
 */
export function useGlobalDialogManager(): DialogManagerInstance {
  if (!globalDialogManagerInstance) {
    globalDialogManagerInstance = createDialogManager();
  }
  return globalDialogManagerInstance;
}
